<template>
  <Teleport to="body">
    <!-- Notification -->
    <Notification ref="notificationRef" />

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div v-if="props.show" class="fixed inset-0 z-50 overflow-y-auto">
        <!-- Fond sombre -->
        <div
          class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          @click="handleClose"
        ></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            @click.stop
          >
            <!-- Header -->
            <div
              class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between"
            >
              <h2 class="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Bug class="w-5 h-5 text-bdc-ocre" />
                Signaler un bug
              </h2>
              <button
                @click="handleClose"
                class="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X class="w-5 h-5" />
              </button>
            </div>

            <!-- Body -->
            <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
              <!-- Screenshot Preview / Editor -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700"> Screenshot </label>
                  <button
                    v-if="screenshotPreview && !isEditingScreenshot"
                    @click="startEditingScreenshot"
                    type="button"
                    class="px-3 py-1 text-xs font-medium text-bdc-ocre hover:bg-bdc-ocre/10 rounded transition-colors flex items-center gap-1"
                  >
                    <Edit class="w-3 h-3" />
                    Annoter
                  </button>
                  <button
                    v-if="isEditingScreenshot"
                    @click="cancelEditingScreenshot"
                    type="button"
                    class="px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  >
                    Annuler
                  </button>
                </div>

                <!-- Éditeur -->
                <div
                  v-if="screenshotPreview && isEditingScreenshot"
                  class="mb-4 border border-gray-300 rounded-lg overflow-hidden"
                  style="max-height: 60vh"
                >
                  <ScreenshotEditor
                    :image-url="screenshotPreview"
                    @save="handleEditorSave"
                    @cancel="cancelEditingScreenshot"
                  />
                </div>

                <!-- Preview simple -->
                <div v-else-if="screenshotPreview" class="mb-4">
                  <ScreenshotPreview :image-url="screenshotPreview" @remove="removeScreenshot" />
                </div>

                <!-- Upload manuel si pas de screenshot (fallback) -->
                <div v-else class="mb-4">
                  <input
                    ref="fileInputRef"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileUpload"
                  />
                  <button
                    type="button"
                    @click="fileInputRef?.click()"
                    class="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload class="w-4 h-4" />
                    Ajouter une image (optionnel)
                  </button>
                  <p class="mt-2 text-xs text-gray-500">
                    Formats acceptés : JPG, PNG, WebP (max 5MB)
                  </p>
                </div>
              </div>

              <!-- Titre -->
              <div>
                <label for="title" class="block text-sm font-medium text-gray-700 mb-1">
                  Titre <span class="text-red-500">*</span>
                </label>
                <input
                  id="title"
                  v-model="form.title"
                  type="text"
                  maxlength="100"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bdc-ocre focus:border-bdc-ocre"
                  placeholder="Décrivez brièvement le bug"
                />
                <p class="mt-1 text-xs text-gray-500">{{ form.title.length }}/100 caractères</p>
              </div>

              <!-- Description -->
              <div>
                <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
                  Description <span class="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  v-model="form.description"
                  rows="4"
                  maxlength="1000"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bdc-ocre focus:border-bdc-ocre"
                  placeholder="Décrivez en détail le problème rencontré..."
                ></textarea>
                <p class="mt-1 text-xs text-gray-500">
                  {{ form.description.length }}/1000 caractères
                </p>
              </div>

              <!-- Catégorie -->
              <div>
                <label for="category" class="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  id="category"
                  v-model="form.category"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bdc-ocre focus:border-bdc-ocre"
                >
                  <option value="probleme">Problème</option>
                  <option value="bug_affichage">Bug d'affichage</option>
                  <option value="bug_fonctionnel">Bug fonctionnel</option>
                  <option value="amelioration_ux">Amélioration UX</option>
                  <option value="fonctionnalite_manquante">Fonctionnalité manquante</option>
                  <option value="commentaire">Commentaire</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <!-- Priorité -->
              <div>
                <label for="priority" class="block text-sm font-medium text-gray-700 mb-1">
                  Priorité
                </label>
                <select
                  id="priority"
                  v-model="form.priority"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bdc-ocre focus:border-bdc-ocre"
                >
                  <option value="low">Faible</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Élevée</option>
                  <option value="critical">Critique</option>
                </select>
              </div>

              <!-- URL (automatique) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"> URL de la page </label>
                <input
                  :value="currentUrl"
                  type="text"
                  disabled
                  class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>

              <!-- Footer -->
              <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  @click="handleClose"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <div class="relative group">
                  <button
                    type="submit"
                    :disabled="!canSubmit || isSubmitting"
                    class="px-4 py-2 text-sm font-medium text-white bg-bdc-ocre hover:bg-bdc-terracotta rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
                  >
                    {{ isSubmitting ? 'Envoi en cours...' : 'Envoyer' }}
                  </button>
                  <!-- Tooltip si le bouton est désactivé -->
                  <div
                    v-if="!canSubmit && !isSubmitting && missingFields.length > 0"
                    class="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl whitespace-normal min-w-[200px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50"
                  >
                    <div class="flex flex-col gap-1.5">
                      <span class="font-semibold text-sm">Champs manquants :</span>
                      <ul class="list-disc list-inside space-y-0.5">
                        <li v-for="field in missingFields" :key="field" class="text-xs">
                          {{ field }}
                        </li>
                      </ul>
                    </div>
                    <!-- Flèche du tooltip -->
                    <div
                      class="absolute top-full right-4 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900"
                    ></div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { CreateBugReportRequest } from '@/api/bug-reports'
import Notification from '@/components/UI/Notification.vue'
import { ScreenshotService } from '@/services/screenshotService'
import type { NotificationType } from '@/types/notification'
import { Bug, Edit, Upload, X } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import ScreenshotEditor from './ScreenshotEditor.vue'
import ScreenshotPreview from './ScreenshotPreview.vue'

const props = withDefaults(
  defineProps<{
    show: boolean
    preCapturedScreenshot?: { dataUrl: string; blob: Blob; mimeType: string } | null
  }>(),
  {
    show: false,
    preCapturedScreenshot: null,
  }
)

const emit = defineEmits<{
  'update:show': [value: boolean]
  close: []
  submitted: []
}>()

const form = ref({
  title: '',
  description: '',
  category: 'probleme' as
    | 'commentaire'
    | 'probleme'
    | 'amelioration_ux'
    | 'fonctionnalite_manquante'
    | 'bug_affichage'
    | 'bug_fonctionnel'
    | 'autre',
  priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
})

const consoleContent = ref<string>('')

const screenshotPreview = ref<string>('')
const screenshotBlob = ref<Blob | null>(null)
const isCapturing = ref(false)
const isSubmitting = ref(false)
const currentUrl = ref(window.location.href)
const fileInputRef = ref<HTMLInputElement | null>(null)
const isEditingScreenshot = ref(false)
const notificationRef = ref<InstanceType<typeof Notification> | null>(null)

const showNotif = (message: string, type: NotificationType = 'success', duration = 4000) => {
  notificationRef.value?.showNotification(message, type, duration)
}

const canSubmit = computed(() => {
  return (
    form.value.title.trim().length >= 3 && form.value.description.trim().length >= 10
    // Screenshot n'est plus obligatoire
  )
})

const missingFields = computed(() => {
  const missing: string[] = []
  if (form.value.title.trim().length < 3) {
    missing.push('Titre (minimum 3 caractères)')
  }
  if (form.value.description.trim().length < 10) {
    missing.push('Description (minimum 10 caractères)')
  }
  return missing
})

async function captureScreenshot() {
  // Protection : ne pas capturer si déjà en cours ou si le formulaire n'est pas visible
  if (isCapturing.value || !props.show) {
    return
  }

  isCapturing.value = true
  try {
    console.log('[BdC-BugReportForm] Capture screenshot via getDisplayMedia...')

    // Utiliser capture (getDisplayMedia par défaut, fallback sur captureViewport)
    const screenshot = await ScreenshotService.capture({
      format: 'png', // PNG sans perte pour préserver la qualité
      quality: 1, // Qualité maximale
    })

    // Vérifier que le formulaire est toujours visible après la capture
    if (!props.show) {
      console.warn('[BdC-BugReportForm] Formulaire fermé pendant la capture, annulation')
      return
    }

    console.log('[BdC-BugReportForm] Screenshot capturé, taille originale:', screenshot.blob.size)

    // Compresser si nécessaire (max 5MB pour préserver la qualité)
    const compressedBlob = await ScreenshotService.compressImage(
      screenshot.blob,
      5 * 1024 * 1024,
      0.95
    )

    screenshotPreview.value = screenshot.dataUrl
    screenshotBlob.value = compressedBlob

    console.log('[BdC-BugReportForm] Screenshot capturé et compressé:', {
      originalSize: screenshot.blob.size,
      compressedSize: compressedBlob.size,
      reduction: `${Math.round((1 - compressedBlob.size / screenshot.blob.size) * 100)}%`,
    })
  } catch (error) {
    // Afficher une erreur si l'utilisateur refuse ou si l'API n'est pas disponible
    const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la capture'
    console.warn('[BdC-BugReportForm] Erreur capture screenshot:', errorMessage)

    // Afficher une notification pour informer l'utilisateur
    if (errorMessage.includes('getDisplayMedia')) {
      showNotif(
        "Capture d'écran annulée ou non disponible. Vous pouvez ajouter une image manuellement.",
        'info',
        5000
      )
    } else {
      showNotif('Erreur lors de la capture. Vous pouvez ajouter une image manuellement.', 'error')
    }
  } finally {
    isCapturing.value = false
  }
}

async function handleSubmit() {
  if (!canSubmit.value) return

  isSubmitting.value = true
  try {
    console.log('[BdC-BugReportForm] Envoi remontée...')

    // Convertir blob en base64 si un screenshot existe
    let base64 = ''
    let mimeType = 'image/png'

    if (screenshotBlob.value) {
      base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Erreur conversion base64'))
          }
        }
        reader.onerror = reject
        reader.readAsDataURL(screenshotBlob.value!)
      })
      mimeType = screenshotBlob.value.type || 'image/png'
    }

    // Capturer le contenu de la console si disponible
    const capturedConsole = captureConsoleContent()

    // Envoyer via l'API (screenshot optionnel)
    const { bugReportsApi } = await import('@/api/bug-reports')
    const payload: CreateBugReportRequest = {
      title: form.value.title,
      description: form.value.description,
      category: form.value.category,
      consoleContent: capturedConsole || null,
      url: currentUrl.value,
      priority: form.value.priority,
    }

    // Ajouter le screenshot seulement s'il existe
    if (base64) {
      payload.screenshot = base64
      payload.screenshotMimeType = mimeType
    }

    await bugReportsApi.create(payload)

    console.log('[BdC-BugReportForm] Remontée envoyée avec succès')
    showNotif('Remontée de bug envoyée avec succès', 'success')

    // Attendre un peu pour que l'utilisateur voie la notification
    setTimeout(() => {
      emit('submitted')
      handleClose()
    }, 500)
  } catch (error) {
    console.error('[BdC-BugReportForm] Erreur envoi remontée:', error)
    showNotif("Erreur lors de l'envoi de la remontée", 'error')
  } finally {
    isSubmitting.value = false
  }
}

function removeScreenshot() {
  screenshotPreview.value = ''
  screenshotBlob.value = null
  isEditingScreenshot.value = false
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

function startEditingScreenshot() {
  isEditingScreenshot.value = true
}

function cancelEditingScreenshot() {
  isEditingScreenshot.value = false
}

async function handleEditorSave(dataUrl: string) {
  try {
    // Convertir dataUrl en blob pour le stockage
    const response = await fetch(dataUrl)
    const blob = await response.blob()

    // Mettre à jour le preview et le blob
    screenshotPreview.value = dataUrl
    screenshotBlob.value = blob
    isEditingScreenshot.value = false

    console.log('[BdC-BugReportForm] Screenshot annoté sauvegardé')
  } catch (err) {
    console.error('[BdC-BugReportForm] Erreur sauvegarde screenshot annoté:', err)
  }
}

const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
  notificationRef.value?.showNotification(message, type)
}

async function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  // Vérifier la taille (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    showNotification('Le fichier est trop volumineux (max 5MB)', 'error')
    return
  }

  // Vérifier le type
  if (!file.type.startsWith('image/')) {
    showNotification('Veuillez sélectionner une image', 'error')
    return
  }

  try {
    // Lire le fichier et créer un blob
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        screenshotPreview.value = reader.result
        screenshotBlob.value = file
        console.log('[BdC-BugReportForm] Image uploadée:', {
          name: file.name,
          size: file.size,
          type: file.type,
        })
      }
    }
    reader.onerror = () => {
      console.error('[BdC-BugReportForm] Erreur lecture fichier')
      showNotification('Erreur lors de la lecture du fichier', 'error')
    }
    reader.readAsDataURL(file)
  } catch (error) {
    console.error('[BdC-BugReportForm] Erreur upload fichier:', error)
    showNotification("Erreur lors de l'upload du fichier", 'error')
  }
}

/**
 * Capture le contenu de la console du navigateur
 * Note: Les navigateurs ne permettent pas d'accéder directement à l'historique complet
 * On capture les erreurs récentes et les informations disponibles
 */
function captureConsoleContent(): string {
  try {
    const logs: string[] = []

    // Capturer les erreurs JavaScript récentes (si disponibles)
    if (window.onerror) {
      // Les erreurs récentes peuvent être capturées via window.onerror
      // mais l'historique complet n'est pas accessible
    }

    // Capturer les erreurs de ressources (images, scripts, etc.)
    window.addEventListener(
      'error',
      event => {
        if (event.error) {
          logs.push(`[ERROR] ${event.error.message || event.message}`)
          if (event.error.stack) {
            logs.push(`Stack: ${event.error.stack}`)
          }
        }
      },
      { once: true }
    )

    // Capturer les erreurs de promesses non gérées
    window.addEventListener(
      'unhandledrejection',
      event => {
        logs.push(`[UNHANDLED REJECTION] ${event.reason}`)
      },
      { once: true }
    )

    // Informations sur le navigateur et l'environnement
    logs.push(`[BROWSER] ${navigator.userAgent}`)
    logs.push(`[URL] ${window.location.href}`)
    logs.push(`[TIMESTAMP] ${new Date().toISOString()}`)

    // Note: L'historique complet de console.log/error/warn n'est pas accessible
    // pour des raisons de sécurité/privacy dans les navigateurs modernes
    const content = logs.join('\n')

    // Limiter à 10000 caractères (limite backend)
    return content.length > 10000 ? content.substring(0, 10000) : content
  } catch (error) {
    console.warn('[BdC-BugReportForm] Erreur capture console:', error)
    return ''
  }
}

function handleClose() {
  emit('update:show', false)
  emit('close')
  // Reset form
  form.value = {
    title: '',
    description: '',
    category: 'probleme',
    priority: 'medium',
  }
  screenshotPreview.value = ''
  screenshotBlob.value = null
  consoleContent.value = ''
}

// Initialiser l'URL au montage
onMounted(() => {
  currentUrl.value = window.location.href
})

// Utiliser le screenshot pré-capturé passé en prop
watch(
  () => props.preCapturedScreenshot,
  screenshot => {
    if (screenshot) {
      console.log('[BdC-BugReportForm] Utilisation du screenshot pré-capturé')
      screenshotPreview.value = screenshot.dataUrl
      screenshotBlob.value = screenshot.blob
    }
  },
  { immediate: true }
)

// Capturer automatiquement si pas de screenshot pré-capturé quand le formulaire s'ouvre
watch(
  () => props.show,
  async isVisible => {
    if (isVisible && !props.preCapturedScreenshot && !screenshotPreview.value) {
      // Essayer de capturer automatiquement
      await captureScreenshot()
    } else if (!isVisible) {
      // Réinitialiser le formulaire quand il se ferme
      handleClose()
    }
  }
)
</script>
